/**
 * Formats a phone number string into a standard format
 * @param phoneNumber - The phone number to format (can be string, null, or undefined)
 * @param config - Configuration options for the formatting
 * @param config.withCountryCode - Whether to include the country code (default: false)
 * @returns The formatted phone number string, or null if the input is invalid
 * @example
 * formatPhoneNumber("+1234567890") // returns "(123) 456-7890"
 * formatPhoneNumber("+1234567890", { withCountryCode: true }) // returns "+1 (123) 456-7890"
 * formatPhoneNumber(null) // returns null
 * formatPhoneNumber("invalid") // returns "invalid"
 */
export const formatPhoneNumber = (
  phoneNumber?: string | null,
  config?: { withCountryCode?: boolean },
) => {
  if (!phoneNumber) return null;
  const { withCountryCode = false } = config || {};
  // Remove any non-digit characters and ensure we only take the last 10 digits
  const cleaned = phoneNumber?.replace(/\D/g, "").slice(-10) || "";

  // Return original string if we don't have exactly 10 digits
  if (cleaned.length !== 10) {
    return phoneNumber;
  }

  // Format into (XXX) XXX-XXXX
  return `${withCountryCode ? "+1 " : ""}(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
};

/**
 * Normalizes a phone number for SMS functionality by adding appropriate country codes
 * @param phoneNumber - The phone number to normalize
 * @returns The normalized phone number with country code for SMS usage
 * @example
 * normalizePhoneWithPlusPrefix("+44123456789") // returns "+44123456789" (international, unchanged)
 * normalizePhoneWithPlusPrefix("1234567890") // returns "+11234567890" (US 10-digit)
 * normalizePhoneWithPlusPrefix("11234567890") // returns "+11234567890" (US with 1 prefix)
 * normalizePhoneWithPlusPrefix("44123456789") // returns "+44123456789" (international without +)
 */
export const normalizePhoneWithPlusPrefix = (phoneNumber: string): string => {
  // If already has country code, use as-is
  if (phoneNumber.startsWith("+")) {
    return phoneNumber;
  }

  // Clean number to check if it's a valid US 10-digit number
  const cleanedNumber = phoneNumber.replace(/\D/g, "");

  // If it's exactly 10 digits, assume it's a US number and add +1
  if (cleanedNumber.length === 10) {
    return `+1${cleanedNumber}`;
  }

  // If it starts with "1" and has 11 digits total, it's likely US format
  if (cleanedNumber.startsWith("1") && cleanedNumber.length === 11) {
    return `+${cleanedNumber}`;
  }

  // Otherwise, assume it's an international number and add + if missing
  return phoneNumber.startsWith("+") ? phoneNumber : `+${phoneNumber}`;
};
